import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  andAllWhere,
  andWhere,
  buildQuery,
  CombinedFilter,
  FilterFn,
  noop,
  Ops,
  orderBy,
  OrderVariant,
  orWhere,
  page,
  where,
} from '@albatrosdeveloper/ave-utils-npm/lib/utils/query.util';
import { IPagination, IPaginationResult } from 'app/common/interfaces';
import { AbstractService } from 'app/services/abstract.service';
import { environment as env } from 'environments/environment.prod';
import { LiquidationAttributes, LiquidationAttributesReport, LiquidationsResponse } from './interfaces/liquidation.interface';
import { sortBy } from 'lodash';
import { IFilters } from '../../filters/interfaces/filterInterface';

const apiUrl = env.API_AVE_LIQUIDATION_URL;
const apiUrlOrdersReport = env.API_AVE_ORDER_REPORT_URL;
const moduleName = 'liquidation';
const moduleNameOrdersReport = 'api/order_report/liquidation';

@Injectable({
  providedIn: 'root',
})
export class LiquidationService extends AbstractService<
  LiquidationAttributes,
  Partial<LiquidationAttributes>,
  Partial<LiquidationAttributes>
> {
  currentFilter: CombinedFilter<LiquidationAttributesReport>;
  currentFilterData: {search: string};
  constructor(http: HttpClient) {
    super(apiUrl, moduleName, http);
  }

  /**
   * Obtiene las liquidaciones filtradas por tripId con paginación
   * @param tripId ID del viaje para filtrar las liquidaciones
   * @param options Opciones de paginación y ordenamiento
   * @returns Observable con los resultados paginados
   */
  getLiquidationByTripId(
    tripId: number,
    options: IPagination<LiquidationAttributes>
  ): Observable<IPaginationResult<LiquidationAttributes>> {
    // const query = buildQuery<LiquidationAttributes>(
    //   where('_deleted', Ops.eq(false)),
    //   andAllWhere('tripId', Ops.eq(tripId)),
    //   orderBy(
    //     options.sort ?? ('createdAt' as keyof LiquidationAttributes),
    //     options.sortDirection === 'ASC' ? OrderVariant.Asc : OrderVariant.Desc
    //   ),
    //   page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    // );

    const query = {
      where: [
        {
          tripId: {
            searchType: 'eq',
            value: {
              value: tripId,
            },
          },
        },
      ],
      page: {
        pageIndex: 1,
        size: 100,
      },
    };

    const API_URL = `${apiUrl}api/${moduleName}/withCount?filter=${encodeURIComponent(
      JSON.stringify(query)
    )}`;

    return this.http.get<IPaginationResult<LiquidationAttributes>>(API_URL);
  }

  /**
   * Obtiene las liquidaciones filtradas por tripId con paginación
   * @param options Opciones de paginación y ordenamiento
   * @returns Observable con los resultados paginados
   */
  getAllLiquidationReport(
    tripId: number,
    options: IPagination<LiquidationAttributesReport>,
    dataToFilter?: IFilters
  ): Observable<IPaginationResult<LiquidationAttributesReport>> {

    const query = buildQuery<LiquidationAttributesReport>(
          where("_deleted", Ops.eq(false)),
          where(
                  "serial" as keyof LiquidationAttributesReport,
                  Ops.like(options.search ?? "", "i")
                ),
          andAllWhere(
            "tripId" as keyof LiquidationAttributesReport,
            Ops.eq(tripId)
          ),
          dataToFilter && dataToFilter.start && dataToFilter.end
          ? andAllWhere("createdAt", Ops.btwnDates(dataToFilter.start, dataToFilter.end))
          : noop(),
    
          dataToFilter.masterUserId && dataToFilter.masterUserId.length > 0
            ? andAllWhere("tripUser.user_login.username" as keyof LiquidationAttributesReport, Ops.in(...dataToFilter.masterUserId))
            : noop(),
    
          orderBy(
            options.sort ?? ("dateHourStartTeoric" as keyof LiquidationAttributesReport),
            options.sortDirection == "ASC" ? OrderVariant.Asc : OrderVariant.Desc
          ),
          page(options.currentPage ?? 1, options.limitPerPage ?? 100)
        );

    const API_URL = `${apiUrl}api/${moduleName}/withCount?filter=${encodeURIComponent(
      JSON.stringify(query)
    )}`;

    return this.http.get<IPaginationResult<LiquidationAttributesReport>>(API_URL);
  }

  /**
   * Obtiene todas las liquidaciones con filtros opcionales
   * @param options Opciones de paginación y ordenamiento
   * @param tripIds Array de IDs de viajes para filtrar (opcional)
   * @param statusIds Array de IDs de estados para filtrar (opcional)
   * @returns Observable con los resultados paginados
   */
  findAllLiquidationsWithCount(
    options: IPagination<LiquidationAttributes>,
    tripIds?: number[],
    statusIds?: number[]
  ): Observable<IPaginationResult<LiquidationAttributes>> {
    let queryParts: any[] = [
      where('_deleted', Ops.eq(false)),
      where('code', Ops.like(options.search ?? '', 'i')),
    ];

    if (tripIds && tripIds.length > 0) {
      queryParts.push(andAllWhere('tripId', Ops.in(...tripIds)));
    }

    if (statusIds && statusIds.length > 0) {
      queryParts.push(andAllWhere('statusId', Ops.in(...statusIds)));
    }

    queryParts.push(
      orderBy(
        options.sort ?? ('createdAt' as keyof LiquidationAttributes),
        options.sortDirection === 'ASC' ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    const query = buildQuery<LiquidationAttributes>(...queryParts);
    // @ts-ignore
    this.currentFilter = query;

    const API_URL = `${apiUrl}api/${moduleName}/withCount?filter=${encodeURIComponent(
      JSON.stringify(query)
    )}`;

    return this.http.get<IPaginationResult<LiquidationAttributes>>(API_URL);
  }

  /**
   * Obtiene una liquidación específica por su ID
   * @param liquidationId ID de la liquidación
   * @returns Observable con la liquidación
   */
  getLiquidationById(liquidationId: string): Observable<LiquidationAttributes> {
    const API_URL = `${apiUrl}api/${moduleName}/${liquidationId}`;
    return this.http.get<LiquidationAttributes>(API_URL);
  }

  /**
   * Obtiene todas las liquidaciones con filtros aplicados directamente
   * @param options Opciones de paginación, búsqueda y ordenamiento
   * @param filters Filtros a aplicar
   * @returns Observable con los resultados paginados
   */
  getAllLiquidationsWithFilters(
    options: IPagination<LiquidationAttributesReport>,
    filters: IFilters
): Observable<LiquidationsResponse> {    
    const query = buildQuery<LiquidationAttributesReport>(       
        where('_deleted', Ops.eq(false)), 
        // andWhere("serial", Ops.like(options.search ?? "", "i")),
        // orWhere('trip.id' as keyof LiquidationAttributesReport, Ops.like(options.search ?? "", "i")),

        filters.assignedUserId && filters.assignedUserId.length > 0
        ? andAllWhere('tripUser.user_login.username' as keyof LiquidationAttributesReport, Ops.in(...filters.assignedUserId))
            : noop(),

        filters.start && filters.end
            ? andAllWhere('createdAt' as keyof LiquidationAttributesReport, Ops.btwnDates(filters.start, filters.end))
            : noop(),

        orderBy(
            options.sort ?? ("createdAt" as keyof LiquidationAttributesReport),
            options.sortDirection == "ASC" ? OrderVariant.Asc : OrderVariant.Desc
          ),
          page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    const queryTrip = buildQuery<LiquidationAttributesReport>(  
        where('_deleted', Ops.eq(false)),       
        filters.startTrip && filters.endTrip
              ? andAllWhere("dateHourStartTeoric" as keyof LiquidationAttributesReport, Ops.btwnDates(filters.startTrip, filters.endTrip))
              : noop(),

        filters.end_startTrip && filters.end_endTrip
              ? andAllWhere("dateHourEndTeoric" as keyof LiquidationAttributesReport, Ops.btwnDates(filters.end_startTrip, filters.end_endTrip))
              : noop(),
        
        filters.companyId
            ? andAllWhere('company.id' as keyof LiquidationAttributesReport, Ops.eq(filters.companyId))
            : noop(),

        filters.salesOfficeId
            ? andAllWhere('salesOffice.id' as keyof LiquidationAttributesReport, Ops.in(...filters.salesOfficeId))
            : noop(),               
    );
    this.currentFilter = query;
    this.currentFilterData = { search: options.search };

    const API_URL = `${apiUrlOrdersReport}${moduleNameOrdersReport}/withCountByTrip`;

    return this.http.post<LiquidationsResponse>(API_URL, { filter: query, filterTrip: queryTrip });
}

  /**
   * Método wrapper para exports que maneja la llamada HTTP con el query proporcionado
   */
  filterForExport(exportQuery: CombinedFilter<LiquidationAttributesReport>): Observable<IPaginationResult<LiquidationAttributesReport>> {
    const API_URL = `${apiUrl}api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(exportQuery))}`;
    return this.http.get<IPaginationResult<LiquidationAttributesReport>>(API_URL, {
      headers: { 'x-ave-ctx': 'export' }
    });
  }
}
